import { getApp, getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { connectDatabaseEmulator, getDatabase, type Database } from 'firebase/database';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';

import { loadRuntimeConfig, type RuntimeConfig } from './runtime-config';

export interface FrpFirebaseClient {
  app: FirebaseApp;
  auth: Auth;
  db: Database;
  functions: Functions;
  storage: FirebaseStorage;
  config: RuntimeConfig;
  environment: RuntimeConfig['environment'];
}

let firebaseClientPromise: Promise<FrpFirebaseClient> | null = null;
let emulatorsConnected = false;

const LOCAL_EMULATOR_HOST = '127.0.0.1';
const LOCAL_EMULATOR_PORTS = {
  auth: 9099,
  database: 9002,
  functions: 5003,
  storage: 9197,
} as const;

export async function initializeFrpFirebase(): Promise<FrpFirebaseClient> {
  if (!firebaseClientPromise) {
    firebaseClientPromise = loadRuntimeConfig().then((config) => {
      const app = getApps().length ? getApp() : initializeApp(config.firebase);
      const auth = getAuth(app);
      const db = getDatabase(app);
      const functions = getFunctions(app, 'us-central1');
      const storage = getStorage(app);

      if (shouldUseLocalEmulators(config)) {
        connectLocalEmulators({ auth, db, functions, storage });
      }

      return {
        app,
        auth,
        db,
        functions,
        storage,
        config,
        environment: config.environment
      };
    });
  }

  return firebaseClientPromise;
}

function shouldUseLocalEmulators(config: Pick<RuntimeConfig, 'environment' | 'useLocalEmulators'>): boolean {
  if (typeof window === 'undefined' || config.environment !== 'development' || !config.useLocalEmulators) {
    return false;
  }

  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

function connectLocalEmulators(client: Pick<FrpFirebaseClient, 'auth' | 'db' | 'functions' | 'storage'>): void {
  if (emulatorsConnected) {
    return;
  }

  connectAuthEmulator(
    client.auth,
    `http://${LOCAL_EMULATOR_HOST}:${LOCAL_EMULATOR_PORTS.auth}`,
    { disableWarnings: true }
  );
  connectDatabaseEmulator(client.db, LOCAL_EMULATOR_HOST, LOCAL_EMULATOR_PORTS.database);
  connectFunctionsEmulator(client.functions, LOCAL_EMULATOR_HOST, LOCAL_EMULATOR_PORTS.functions);
  connectStorageEmulator(client.storage, LOCAL_EMULATOR_HOST, LOCAL_EMULATOR_PORTS.storage);

  emulatorsConnected = true;
}
